import React, { useState } from "react";
import api from "../../../../utils/api";
import { Chip, Sub, fmtD, fmtDT } from "./ui";

const errMsg = (e, fallback) => e.response?.data?.error || fallback;

// Pallet comments: add (with @name@company.com mentions → queued email), edit, pin, resolve, delete.
// Comments imported from the sheet can be edited too — that changes the Control Tower copy, not the Excel cell.
export default function PalletComments({ p, lines, comments, ry, onChanged }) {
    const [body, setBody] = useState("");
    const [scope, setScope] = useState("");
    const [editing, setEditing] = useState(null);
    const [editText, setEditText] = useState("");
    const [confirmDel, setConfirmDel] = useState(null);
    const [busy, setBusy] = useState(false);
    const [err, setErr] = useState("");
    const base = `/api/excel-ingestion/pallets/${encodeURIComponent(p.id)}/comments`;
    const skuOf = id => (id ? id.split("-").pop() : null);

    const run = async (fn, fallback) => {
        setBusy(true); setErr("");
        try { await fn(); onChanged(); return true; }
        catch (e) { setErr(errMsg(e, fallback)); return false; }
        finally { setBusy(false); }
    };
    const post = async e => {
        e.preventDefault();
        if (!body.trim()) return;
        if (await run(() => api.post(base, { body: body.trim(), lineId: scope || undefined }), "Could not post the comment")) setBody("");
    };
    const patch = (id, data) => run(() => api.patch(`${base}/${id}`, data), "Could not update the comment");
    const remove = id => run(() => api.delete(`${base}/${id}`), "Could not delete the comment");

    const field = "w-full bg-[#0D1520] border border-[#1F2937] rounded-lg px-2.5 py-1.5 text-[12px] text-slate-200 focus:border-blue-500/50 outline-none";
    const link = "text-[11px] text-slate-400 hover:text-white";
    return (
        <section className="bg-[#111827] border border-[#1F2937] rounded-xl p-4 space-y-3" aria-labelledby="cm-h">
            <h3 id="cm-h" className="text-sm font-bold text-white">Comments <span className="text-slate-500 font-normal">{comments.length}</span></h3>
            <form onSubmit={post} className="space-y-2">
                <label htmlFor="cm-body" className="sr-only">New comment</label>
                <textarea id="cm-body" rows={3} maxLength={4000} value={body} onChange={e => setBody(e.target.value)}
                    placeholder="Add a comment. Type @name@company.com to email someone." className={`${field} resize-y`}/>
                <div className="flex items-center justify-between gap-2">
                    <select aria-label="Comment applies to" value={scope} onChange={e => setScope(e.target.value)} className={`${field} max-w-[220px]`}>
                        <option value="">Whole pallet</option>
                        {lines.map(l => <option key={l.id} value={l.id}>{l.sku} · {(l.description || "").slice(0, 28)}</option>)}
                    </select>
                    <button type="submit" disabled={!body.trim() || busy} className="px-3 py-1.5 rounded-lg bg-blue-600 text-[12px] font-semibold text-white hover:bg-blue-500 disabled:opacity-40">Post</button>
                </div>
                {err && <p className="text-[11px] text-red-400" role="alert">{err}</p>}
            </form>
            <ul className="space-y-2">
                {comments.map(c => (
                    <li key={c.id} className={`border rounded-lg p-2.5 space-y-1 ${c.pinned ? "border-amber-500/40 bg-amber-500/5" : "border-[#1F2937] bg-[#0D1520]"} ${c.resolved ? "opacity-60" : ""}`}>
                        <div className="flex flex-wrap items-center gap-1.5">
                            <b className="text-[12px] text-slate-200">{c.author?.name || "—"}</b>
                            {c.source?.kind === "excel" && <Chip>from sheet · {c.source.column?.split(":")[0]}</Chip>}
                            {skuOf(c.lineId) && <Chip tone="info">{skuOf(c.lineId)}</Chip>}
                            {c.pinned && <Chip tone="warn">pinned</Chip>}
                            {c.resolved && <Chip tone="good">resolved</Chip>}
                            <Sub>{c.source?.kind === "excel" ? `upload ${fmtD(c.createdAt, ry)}` : fmtDT(c.createdAt)}{c.editedAt ? " · edited" : ""}</Sub>
                        </div>
                        {editing === c.id ? (
                            <div className="space-y-2">
                                <label htmlFor={`ed-${c.id}`} className="sr-only">Edit comment</label>
                                <textarea id={`ed-${c.id}`} rows={4} maxLength={4000} value={editText} onChange={e => setEditText(e.target.value)} className={`${field} resize-y`}/>
                                <div className="flex justify-end gap-2">
                                    <button type="button" className={link} onClick={() => setEditing(null)}>Cancel</button>
                                    <button type="button" disabled={busy || !editText.trim()} onClick={async () => { if (await patch(c.id, { body: editText })) setEditing(null); }}
                                        className="px-2.5 py-1 rounded bg-blue-600 text-[11px] font-semibold text-white disabled:opacity-40">Save</button>
                                </div>
                                {c.source?.kind === "excel" && <Sub>The Control Tower copy changes. The original Excel cell stays as uploaded.</Sub>}
                            </div>
                        ) : (
                            <p className="text-[12px] text-slate-300 whitespace-pre-wrap break-words">{c.body}</p>
                        )}
                        {c.mentions?.length > 0 && <Sub>Notified: {c.mentions.join(", ")}</Sub>}
                        {editing !== c.id && (
                            <div className="flex flex-wrap gap-3 pt-0.5">
                                <button type="button" className={link} onClick={() => { setEditing(c.id); setEditText(c.body); }}>Edit</button>
                                <button type="button" className={link} onClick={() => patch(c.id, { pinned: !c.pinned })}>{c.pinned ? "Unpin" : "Pin"}</button>
                                <button type="button" className={link} onClick={() => patch(c.id, { resolved: !c.resolved })}>{c.resolved ? "Reopen" : "Resolve"}</button>
                                {confirmDel === c.id ? (
                                    <span className="text-[11px] text-slate-400">Delete this comment?{" "}
                                        <button type="button" className="text-red-400 hover:underline" onClick={async () => { await remove(c.id); setConfirmDel(null); }}>Delete</button>{" "}
                                        <button type="button" className={link} onClick={() => setConfirmDel(null)}>Keep</button>
                                    </span>
                                ) : (
                                    <button type="button" className={link} onClick={() => setConfirmDel(c.id)}>Delete</button>
                                )}
                            </div>
                        )}
                    </li>
                ))}
                {comments.length === 0 && <li className="text-[11px] text-slate-500">No comments yet.</li>}
            </ul>
        </section>
    );
}
